import { decodeProtectedHeader, importSPKI, jwtVerify } from 'jose';
import { EMPTY_ISSUER_INFO, type IssuerInfo, parseIssuerInfo } from '@domain/legal/IssuerInfo';
import {
  ACTIVATION_AUDIENCE,
  ACTIVATION_ISSUER,
  resolveLicensingApiBases,
} from '@infra/licence/activationConstants';
import { resolvePublicKey } from '@infra/licence/PublicKeyRegistry';
import { SecureTenantStore } from '@infra/persistence/SecureTenantStore';

/**
 * Informations de l'émetteur pour les documents du poste (CGU, consentement, pieds de page).
 * - Source : copie SIGNÉE (ES256, même clé que les certificats) publiée par le serveur de licences, relayant
 *   l'écran « Informations de l'émetteur » de la console. Elle est vérifiée AVANT tout usage.
 * - Hors ligne : la dernière copie vérifiée est conservée sur le poste (SQLite chiffré) ; une copie expirée mais
 *   correctement signée reste utilisable (ce sont des mentions légales, pas un droit d'accès).
 * - Sans copie : informations vides => formulations neutres (jamais de valeur inventée).
 */
export const ISSUER_INFO_EVENT = 'nexxus:issuer-info';
const DEVICE_SCOPE = '__device__';
const NAMESPACE = 'issuer_info_signed';
/** Une copie signée est acceptée jusqu'à un an après son expiration (mentions légales : pas de coupure hors ligne). */
const MAX_STALENESS_SEC = 365 * 86_400;

let current: IssuerInfo = EMPTY_ISSUER_INFO;
let currentVersion = 0;

export function getIssuerInfo(): IssuerInfo {
  return current;
}

export function getIssuerInfoVersion(): number {
  return currentVersion;
}

/** Injection explicite (tests, ou valeurs déjà vérifiées). */
export function setIssuerInfo(info: IssuerInfo, version = 0): void {
  current = info;
  currentVersion = version;
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(ISSUER_INFO_EVENT));
}

export function resetIssuerInfo(): void {
  setIssuerInfo(EMPTY_ISSUER_INFO, 0);
}

export type IssuerTokenResult =
  { ok: true; info: IssuerInfo; version: number } | { ok: false; reason: string };

export interface IssuerTokenOptions {
  nowSec?: number;
  keyResolver?: (kid: string | undefined) => string | null;
}

/** Vérifie la copie signée : algorithme épinglé, émetteur, audience, type « issuer ». */
export async function verifyIssuerToken(
  jwt: unknown,
  options: IssuerTokenOptions = {},
): Promise<IssuerTokenResult> {
  if (typeof jwt !== 'string' || jwt.split('.').length !== 3)
    return { ok: false, reason: 'absent_ou_mal_forme' };
  try {
    const { kid, alg } = decodeProtectedHeader(jwt);
    if (alg !== 'ES256') return { ok: false, reason: 'algorithme_refuse' };
    const pem = (options.keyResolver ?? resolvePublicKey)(kid);
    if (!pem) return { ok: false, reason: 'cle_inconnue' };
    const key = await importSPKI(pem, 'ES256');
    const nowSec = options.nowSec ?? Math.floor(Date.now() / 1000);
    const { payload } = await jwtVerify(jwt, key, {
      algorithms: ['ES256'],
      issuer: ACTIVATION_ISSUER,
      audience: ACTIVATION_AUDIENCE,
      currentDate: new Date(nowSec * 1000),
      clockTolerance: MAX_STALENESS_SEC,
      requiredClaims: ['exp', 'iat'],
    });
    if (payload.typ !== 'issuer') return { ok: false, reason: 'type_incorrect' };
    const version = typeof payload.issuer_version === 'number' ? payload.issuer_version : 0;
    return { ok: true, info: parseIssuerInfo(payload.issuer), version };
  } catch (err) {
    return { ok: false, reason: (err as { code?: string }).code ?? 'signature_invalide' };
  }
}

/** Charge la dernière copie vérifiée conservée sur le poste (démarrage, même hors ligne). */
export async function initIssuerInfo(): Promise<void> {
  try {
    const stored = await SecureTenantStore.getJson<string | null>(DEVICE_SCOPE, NAMESPACE, null);
    if (!stored) return;
    const result = await verifyIssuerToken(stored);
    if (result.ok) setIssuerInfo(result.info, result.version);
  } catch {
    /* aucune copie exploitable : formulations neutres */
  }
}

/**
 * Récupère la copie signée auprès du serveur de licences (si le réseau répond), la vérifie, la conserve et
 * la rend disponible. Silencieux en cas d'échec (hors ligne, serveur indisponible).
 */
export async function refreshIssuerInfo(fetchImpl: typeof fetch = fetch): Promise<boolean> {
  for (const base of resolveLicensingApiBases()) {
    try {
      const response = await fetchImpl(`${base}/v1/public/issuer/signed`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) continue;
      const body = (await response.json()) as { token?: unknown };
      const result = await verifyIssuerToken(body.token);
      if (!result.ok) continue;
      setIssuerInfo(result.info, result.version);
      try {
        await SecureTenantStore.setJson(DEVICE_SCOPE, NAMESPACE, body.token);
      } catch {
        /* conservation impossible : la copie reste utilisable pour cette session */
      }
      return true;
    } catch {
      /* hors ligne ou serveur injoignable : on essaie la base suivante */
    }
  }
  return false;
}
